import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import { sanitizeHtml } from "@/lib/sanitize-html";

export type PostCard = {
  slug: string;
  h1_title: string;
  excerpt: string | null;
  cover_image_url: string | null;
  cover_image_alt: string | null;
  published_at: string | null;
  category: { name: string; slug: string } | null;
};

export type PostFull = PostCard & {
  content_html: string;
  seo_title: string | null;
  meta_description: string | null;
  updated_at: string;
};

function publicClient() {
  const key = process.env["SUPABASE_PUBLISHABLE_KEY"]!;
  const url = process.env["SUPABASE_URL"]!;
  return createClient<Database>(url, key, {
    auth: { persistSession: false, autoRefreshToken: false },
    global: {
      fetch: (input, init) => {
        const h = new Headers(init?.headers);
        if (key.startsWith("sb_") && h.get("Authorization") === `Bearer ${key}`) h.delete("Authorization");
        h.set("apikey", key);
        return fetch(input, { ...init, headers: h });
      },
    },
  });
}

const CARD = "slug,category_id,h1_title,excerpt,cover_image_url,cover_image_alt,published_at,category:categories(name,slug)";

export const listPosts = createServerFn({ method: "GET" }).handler(async () => {
  const supabase = publicClient();
  const { data, error } = await supabase
    .from("posts")
    .select(CARD)
    .eq("status", "published").is("deleted_at", null)
    .order("published_at", { ascending: false });
  if (error) return { posts: [] as PostCard[], error: error.message };
  return { posts: (data ?? []) as unknown as PostCard[], error: null as string | null };
});

export const getPost = createServerFn({ method: "GET" })
  .inputValidator((data: { slug: string }) => data)
  .handler(async ({ data }) => {
    const supabase = publicClient();
    const { data: post } = await supabase
      .from("posts")
      .select(`${CARD},content_html,seo_title,meta_description,updated_at`)
      .eq("slug", data.slug)
      .eq("status", "published").is("deleted_at", null)
      .maybeSingle();
    if (!post) return { post: null as PostFull | null, related: [] as PostCard[] };

    const p = post as unknown as PostFull & { category: { name: string; slug: string } | null };
    p.content_html = sanitizeHtml(p.content_html ?? "");
    const shuffle = (list: PostCard[]) => {
      const copy = [...list];
      for (let i = copy.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [copy[i]!, copy[j]!] = [copy[j]!, copy[i]!];
      }
      return copy;
    };

    let related: PostCard[] = [];
    const categoryId = (p as unknown as { category_id: string | null }).category_id;
    if (categoryId) {
      const { data: rel } = await supabase
        .from("posts")
        .select(CARD)
        .eq("status", "published").is("deleted_at", null)
        .neq("slug", data.slug)
        .eq("category_id", categoryId);
      related = shuffle((rel ?? []) as unknown as PostCard[]).slice(0, 3);
    }
    if (related.length < 3) {
      const { data: fill } = await supabase
        .from("posts")
        .select(CARD)
        .eq("status", "published").is("deleted_at", null)
        .neq("slug", data.slug)
        .order("published_at", { ascending: false })
        .limit(12);
      const seen = new Set(related.map((r) => r.slug));
      for (const item of shuffle((fill ?? []) as unknown as PostCard[])) {
        if (related.length >= 3) break;
        if (!seen.has(item.slug)) {
          seen.add(item.slug);
          related.push(item);
        }
      }
    }
    return { post: p as PostFull, related: related.slice(0, 3) };
  });

export const listPostSlugs = createServerFn({ method: "GET" }).handler(async () => {
  const supabase = publicClient();
  const { data } = await supabase
    .from("posts")
    .select("slug,updated_at")
    .eq("status", "published").is("deleted_at", null)
    .order("published_at", { ascending: false });
  return (data ?? []) as { slug: string; updated_at: string }[];
});
